import { useHistory, useRouteMatch } from 'react-router-dom';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faPlus, faX } from '@fortawesome/pro-regular-svg-icons';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as RemoveCompanyDialog from 'assets/js/pages/contacts/RemoveCompanyDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { Contact } from 'types/domain';

const columnHelper = createColumnHelper<Pick<Contact, 'id' | 'company_name' | 'primary_email'>>();

const ContactCompanies = () => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const history = useHistory();
    const match = useRouteMatch();

    const table = useReactTable({
        data: contact.associated_companies ?? [],
        columns: [
            columnHelper.accessor('company_name', {
                header: 'Company',
                cell: () => <Table.CellDataOrEmpty className={'font-medium'} />,
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const company = info.row.original;
                    return (
                        <DropdownMenu.Root key={company.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                <RemoveCompanyDialog.Root company={company}>
                                    <RemoveCompanyDialog.Trigger asChild>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="flex gap-2"
                                        >
                                            <FontAwesomeIcon icon={faX} />
                                            Remove company
                                        </DropdownMenu.Item>
                                    </RemoveCompanyDialog.Trigger>
                                </RemoveCompanyDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        enableColumnResizing: false,
        meta: {
            enableColumnOrder: true,
            to: cell => `/accounts/${accountId}/companies/${cell.row.original.id}`,
        },
    });

    return (
        <Table.Root table={table} variant="bordered">
            <Box className="flex flex-col overflow-hidden">
                <div className={'flex flex-col'}>
                    <div className={'flex justify-between gap-4 border-b border-b-gray-100 p-4'}>
                        <div className="flex items-center gap-2 text-lg font-medium">
                            <FontAwesomeIcon className="text-gray-500" icon={faBuilding} />{' '}
                            Companies
                        </div>
                        <div className={'flex flex-wrap gap-1.5'}>
                            <Button
                                variant="outline"
                                color={'neutral'}
                                size="sm"
                                onClick={() => history.push(`${match.url}/add-company`)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add company
                            </Button>
                        </div>
                    </div>
                </div>
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon>
                        <FontAwesomeIcon icon={faBuilding} />
                    </Table.EmptyIcon>
                    <Table.EmptyTitle>No companies found</Table.EmptyTitle>
                    <Table.EmptyDescription>
                        Add a company to establish {contact.first_name_display}'s associated
                        companies
                    </Table.EmptyDescription>
                </Table.Empty>
            </Box>
        </Table.Root>
    );
};

export { ContactCompanies };
